// Story 11.3 — Hook que orquesta el sync editor↔diagrama.
// La pure-logic (`buildLineMap`, `resolveHighlightedStep`, `applyCursorMove`)
// vive en `lib/sync-state.ts` + `components/editor/diagram/line-map.ts`.

import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { buildLineMap, type LineMapResult } from '@/components/editor/diagram/line-map';
import { applyCursorMove, initialSyncState, type SyncState } from '@/lib/sync-state';

const CURSOR_DEBOUNCE_MS = 80;

export interface UseEditorDiagramSyncResult {
  /** Step actualmente highlighted en el diagrama. */
  highlightedStepId: string | null;
  /** Map stepId → línea de inicio (para click en nodo → revealLine). */
  stepToLine: Map<string, number>;
  /** Callback para que CodeEditor reporte cambios de cursor. */
  reportCursorPosition(line: number | null): void;
}

/**
 * Construye y mantiene el line map del contenido (deferred para no
 * bloquear typing) y deriva `highlightedStepId` desde la posición del cursor
 * con debounce de 80ms.
 *
 * Si `content` cambia y el nuevo YAML rompe parse, se preserva el último
 * `lineMap` válido (UX: no parpadear el highlight mientras el dev tipea).
 *
 * P-413 (review 11.3): cuando cambia `pathKey`, reseteamos el cache de
 * `lastValidMapRef` para que el lineMap del archivo previo no se mezcle
 * con el del archivo nuevo.
 */
export function useEditorDiagramSync(
  content: string,
  pathKey: string | null = null,
): UseEditorDiagramSyncResult {
  const deferredContent = useDeferredValue(content);

  const lastValidMapRef = useRef<LineMapResult>({
    lineToStep: new Map(),
    stepToLine: new Map(),
    ok: false,
  });
  const prevPathRef = useRef<string | null>(pathKey);
  if (prevPathRef.current !== pathKey) {
    prevPathRef.current = pathKey;
    // P-413: reset el cache de last-valid al cambiar de archivo. Sin esto,
    // si el user pasaba de un flow A válido a un flow B con YAML inválido,
    // el lineMap mostraba steps del archivo A.
    lastValidMapRef.current = { lineToStep: new Map(), stepToLine: new Map(), ok: false };
  }

  const lineMap = useMemo<LineMapResult>(() => {
    const fresh = buildLineMap(deferredContent);
    if (fresh.ok) {
      lastValidMapRef.current = fresh;
      return fresh;
    }
    // YAML inválido o sin steps — preservar el último válido para que el
    // highlight no parpadee.
    return lastValidMapRef.current;
  }, [deferredContent]);

  // P-414 (review 11.3): el `setTimeout` callback debajo capturaba el
  // `lineMap` del momento de invocar `reportCursorPosition`. Si el user
  // editaba el YAML entre el cursor move y el debounce-fire (80ms), el
  // lineMap usado para resolver el step quedaba stale. La ref siempre
  // apunta al lineMap más reciente del render.
  const lineMapRef = useRef(lineMap);
  useEffect(() => {
    lineMapRef.current = lineMap;
  }, [lineMap]);

  const [state, setState] = useState<SyncState>(initialSyncState);
  const debounceRef = useRef<number | null>(null);
  const pendingLineRef = useRef<number | null>(null);

  function reportCursorPosition(line: number | null): void {
    pendingLineRef.current = line;
    if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const next = pendingLineRef.current;
      // P-414: usar la ref para asegurar el lineMap más fresco.
      setState((prev) => applyCursorMove(prev, next, lineMapRef.current));
      debounceRef.current = null;
    }, CURSOR_DEBOUNCE_MS);
  }

  // Cleanup del timer al unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    };
  }, []);

  // Si cambia el lineMap (por content edits) sin cursor move, re-resolver
  // con el cursor previo para que el highlight se actualice si los stepIds
  // cambiaron de línea.
  useEffect(() => {
    setState((prev) => applyCursorMove(prev, prev.cursorLine, lineMap));
  }, [lineMap]);

  return {
    highlightedStepId: state.highlightedStepId,
    stepToLine: lineMap.stepToLine,
    reportCursorPosition,
  };
}
